import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CurriculumInfo } from '../../services/auth';
import './CurriculumSelectorModal.css';

interface CurriculumSelectorModalProps {
  curricula: CurriculumInfo[];
  currentCurriculum?: string | null;
  onSelect: (curriculum: string) => Promise<void>;
  onClose: () => void;
  showAddGrade?: boolean;
}

export const CurriculumSelectorModal: React.FC<CurriculumSelectorModalProps> = ({
  curricula,
  currentCurriculum,
  onSelect,
  onClose,
  showAddGrade = true,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCurriculum, setSelectedCurriculum] = useState<string | null>(null);

  const handleSelect = async (curriculum: string) => {
    if (curriculum === currentCurriculum) {
      onClose();
      return;
    }

    setIsLoading(true);
    setSelectedCurriculum(curriculum);
    try {
      await onSelect(curriculum);
      onClose();
    } catch (err) {
      console.error('Failed to switch curriculum:', err);
    } finally {
      setIsLoading(false);
      setSelectedCurriculum(null);
    }
  };

  const handleAddGrade = () => {
    onClose();
    navigate('/add-grade');
  };

  return (
    <div className="curriculum-modal-overlay" onClick={onClose}>
      <div className="curriculum-modal" onClick={(e) => e.stopPropagation()}>
        <div className="curriculum-modal-header">
          <h2>Select Your Grade</h2>
          <button className="curriculum-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="curriculum-list">
          {curricula.map((c) => (
            <button
              key={c.student_id}
              className={`curriculum-item ${c.curriculum === currentCurriculum ? 'current' : ''}`}
              onClick={() => handleSelect(c.curriculum)}
              disabled={isLoading}
            >
              <div className="curriculum-item-content">
                <span className="curriculum-name">{c.display_name}</span>
                {c.curriculum === currentCurriculum && (
                  <span className="curriculum-current-badge">Current</span>
                )}
              </div>
              {isLoading && selectedCurriculum === c.curriculum && (
                <div className="curriculum-loading-spinner" />
              )}
            </button>
          ))}
        </div>

        {showAddGrade && (
          <div className="curriculum-modal-footer">
            <button
              className="add-grade-button"
              onClick={handleAddGrade}
              disabled={isLoading}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add Another Grade
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
